import type { FormQuestion, FormResponse } from "../../types/form";
import {
  getSavedAlphaIbkrAccountType,
  type AlphaIbkrAccountType,
} from "./alphaIbkrAccountDetails";
import {
  resolveAlphaPlanMixSelection,
  type AlphaPlanMixSelection,
  type AlphaRiskKey,
} from "./alphaRiskMap";
import { getSavedAlphaRiskMatchAnswers } from "./alphaRiskProfile";

export type AlphaManagedMatch =
  | { status: "missing" }
  | { status: "invalid" }
  | {
    status: "ready";
    investmentAmountUsd: number;
    riskKey: AlphaRiskKey;
    selection: AlphaPlanMixSelection;
  };

// A profile-derived managed match has exactly four inputs: investment track,
// risk score, investment amount, and effective account type. Only genuinely
// absent answers return "missing"; malformed or unmappable answers are invalid.
export function resolveAlphaManagedMatch(
  questions: FormQuestion[],
  responses: FormResponse[],
  accountTypeOverride: AlphaIbkrAccountType | null = null,
  investmentAmountOverrideUsd: number | null = null
): AlphaManagedMatch {
  try {
    const riskAnswers = getSavedAlphaRiskMatchAnswers(questions, responses);
    const accountType = accountTypeOverride
      ?? getSavedAlphaIbkrAccountType(questions, responses);
    const hasInvestmentAmountOverride = investmentAmountOverrideUsd !== null;
    if (
      !riskAnswers.investmentTrack
      || !riskAnswers.riskScore
      || !accountType
      || (!hasInvestmentAmountOverride && !riskAnswers.plutusInvestment)
    ) {
      return { status: "missing" };
    }

    const investmentAmountUsd = hasInvestmentAmountOverride
      ? investmentAmountOverrideUsd
      : Number(riskAnswers.plutusInvestment);
    return {
      status: "ready",
      investmentAmountUsd,
      riskKey: riskAnswers.riskScore,
      selection: resolveAlphaPlanMixSelection(
        riskAnswers.investmentTrack,
        accountType,
        riskAnswers.riskScore,
        investmentAmountUsd
      ),
    };
  } catch {
    return { status: "invalid" };
  }
}
